// app/payment-confirm/PaymentConfirmClient.tsx
'use client';

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useMutation } from '@tanstack/react-query';
import { axiosInstance } from '@/lib/axios';
import { isAxiosError, AxiosError } from 'axios';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { EventsSearchBar } from '@/app/(events)/search-bar';
import Image from 'next/image';

type SignupFormData = {
  order_number: string;
  payment_date: string;
  transfer_account_bank: string;
  recipient_account_bank: string;
  image: File;
};

export default function PaymentConfirmClient({ orderNumber }: { orderNumber: string }) {
  const [valueImage, setValueImage] = useState<File | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignupFormData>({
    defaultValues: {
      order_number: orderNumber,
      payment_date: '',
      transfer_account_bank: '',
      recipient_account_bank: '',
      image: undefined as any,
    },
  });

  const mutation = useMutation(async (values: SignupFormData) => {
    await axiosInstance.get('/sanctum/csrf-cookie');

    const formData = new FormData();
    formData.append('order_number', values.order_number);
    formData.append('payment_date', values.payment_date);
    formData.append('transfer_account_bank', values.transfer_account_bank);
    formData.append('recipient_account_bank', values.recipient_account_bank);
    if (valueImage) {
      formData.append('image', valueImage);
    }

    const { data } = await axiosInstance.post('/api/payment-confirmation', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    toast.success('Payment confirmation successful!');
  }, {
    onError: (error) => {
      if (isAxiosError(error)) {
        toast.error(`Error: ${error.response?.data?.message || error.message}`);
      } else {
        toast.error('An unexpected error occurred.');
      }
    },
  });

  const onSubmit = (data: SignupFormData) => {
    mutation.mutate(data);
  };

  return (
      <div>
      <>
            <EventsSearchBar />
            <div className="h-20 xl:h-32" />
      </>
      <div className="fixed dark:hidden inset-x-0 top-0 pointer-events-none -z-10">
            <Image
                  src="/event-background.png"
                  className="object-cover w-full scaleinout"
                  alt="pattern"
                  width={1000}
                  height={1000}
            />
      </div>
      <div className="hidden dark:block dark:fixed inset-x-0 top-[-150px] pointer-events-none -z-10">
            <Image
                  src="/background-dark.png"
                  className="object-cover w-full scaleinout"
                  alt="pattern"
                  width={1000}
                  height={1000}
            />
      </div>

      <h1 className="py-2.5 font-bold capitalize text-2xl dark:text-theme-dark">Konfirmasi Pembayaran</h1>


      <div className='w-full h-max bg-theme-black2 dark:bg-white dark:border dark:border-[#bfbfbf]/50 px-5 py-[40px] rounded-lg'>
            <form onSubmit={handleSubmit(onSubmit)} className='w-full flex flex-col gap-y-[14px]' encType="multipart/form-data">
                  <div className='w-full flex flex-col gap-y-[10px]'>
                        <Label htmlFor="invoice" className="font-semibold text-white dark:text-[#0D0D0D]">
                              Nomor Invoice
                        </Label>
                        <Input
                              id="order_number"
                              placeholder="MEO-XXXXXXXX-XXXXXX"
                              type="text"
                              autoCapitalize="none"
                              autoComplete="order_number"
                              autoCorrect="off"
                              className="text-white dark:text-theme-input border-none bg-theme-input"
                              {...register("order_number", { required: true })}
                              disabled={mutation.isLoading}
                        />
                  </div>
                  <div className='w-full flex flex-col gap-y-[10px]'>
                        <Label htmlFor="payment_date" className="font-semibold text-white dark:text-[#0D0D0D]">
                              Tanggal Pembayaran
                        </Label>
                        <Input
                              id="payment_date"
                              placeholder="dd/mm/yyyy"
                              type="date"
                              autoCapitalize="none"
                              autoComplete="payment_date"
                              autoCorrect="off"
                              className="text-theme-gray border-none w-full"
                              {...register("payment_date", { required: true })}
                        />
                  </div>
                  <div className='w-full flex flex-col gap-y-[10px]'>
                        <Label htmlFor="transfer_account_bank" className="font-semibold text-white dark:text-[#0D0D0D]">
                              Akun Bank Pengirim
                        </Label>
                        <Input
                              id="transfer_account_bank"
                              placeholder="Example : BCA 124353xxx a/n John Doe"
                              type="text"
                              autoCapitalize="none"
                              autoComplete="transfer_account_bank"
                              autoCorrect="off"
                              className="text-white dark:text-theme-input border-none bg-theme-input"
                              {...register("transfer_account_bank", { required: true })}
                        />
                  </div>
                  <div className='w-full flex flex-col gap-y-[10px]'>
                        <Label htmlFor="recipient_account_bank" className="font-semibold text-white dark:text-[#0D0D0D]">
                              Akun Bank Penerima
                        </Label>
                        <Input
                              id="recipient_account_bank"
                              placeholder="Example : BCA 124353xxx a/n John Doe"
                              type="text"
                              autoCapitalize="none"
                              autoComplete="recipient_account_bank"
                              autoCorrect="off"
                              className="text-white dark:text-theme-input border-none bg-theme-input"
                              {...register("recipient_account_bank", { required: true })}
                        />
                  </div>
                  <div className='w-full flex flex-col gap-y-[10px]'>
                        <Label htmlFor="image" className="font-semibold text-white dark:text-[#0D0D0D]">
                              Bukti Pembayaran
                        </Label>
                        <Input
                              id="image"
                              type="file"
                              className="text-theme-gray border-none w-full"
                              // {...register("image", { required: true })}
                              onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    console.log(file);
                                    if (file) {
                                          setValueImage(file);
                                          // setValue("image", file);
                                    }
                              }
                              }
                              accept="image/*"
                              disabled={mutation.isLoading}
                        />
                  </div>
                  <Button disabled={mutation.isLoading} className="h-12 mt-6 text-lg">
                        {mutation.isLoading && <>Loading...</>}
                        Kirim
                  </Button>
            </form>
      </div>
</div>
  );
}
